import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  inject,
  Inject,
  Injectable,
  PLATFORM_ID,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from './config.model';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private readonly title = inject(Title);
  private renderer!: Renderer2;
  private linkTag: HTMLLinkElement | null;
  private readonly meta = inject(Meta);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly config = inject<EnvironmentConfig>(ENVIRONMENT_CONFIG);
  private readonly dom = inject(DOCUMENT);
  private readonly rendererFactory = inject(RendererFactory2);

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    // Find existing link tag if it exists
    this.linkTag = this.dom.querySelector('link[rel="canonical"]');
  }

  init() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        mergeMap((route) => route.data)
      )
      .subscribe((data) => {
        // Note: The browser tab title is handled by CustomPageTitleStrategy.
        // This service focuses on Meta tags and structured data.

        const title = data['title'] || this.config.appName; // Fallback to App Name
        const description =
          data['description'] || `${this.config.appName} - ${this.config.appTitleSuffix}`;
        const keywords =
          data['keywords'] ||
          `${this.config.appName}, DevSpace, Angular, NestJS, React, Golang, Developer Tools, Technical Blog`;

        // We do NOT call this.title.setTitle() here to avoid conflict with TitleStrategy

        this.updateMeta('description', description);
        this.updateMeta('keywords', keywords);

        // Open Graph / Social
        this.updateMeta('og:title', `${title} - ${this.config.appTitleSuffix}`);
        this.updateMeta('og:description', description);
        this.updateMeta('og:type', 'website');

        // Only defaults to summary, but pages can override via data['twitterCard'] if needed
        this.updateMeta('twitter:card', data['twitterCard'] || 'summary_large_image');
        const url = isPlatformBrowser(this.platformId)
          ? window.location.href
          : this.config.coreAppUrl.replace(/\/$/, '') + this.router.url;

        if (!url.endsWith('/not-found')) {
          this.setCanonicalURL(url);
        } else {
          // Actively remove the canonical tag for the not-found page
          this.removeCanonicalURL();
        }

        if (data['schema']) {
          this.setJsonLd(data['schema']);
        }
      });
  }

  updateMeta(name: string, content: string) {
    if (name.startsWith('og:') || name.startsWith('twitter:')) {
      this.meta.updateTag({ property: name, content });
    } else {
      this.meta.updateTag({ name, content });
    }
  }

  setJsonLd(schema: any) {
    if (isPlatformBrowser(this.platformId)) {
      let script = document.getElementById('json-ld') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.id = 'json-ld';
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    }
  }

  setCanonicalURL(url: string): void {
    if (!this.linkTag) {
      // Create the link tag if it doesn't exist
      this.linkTag = this.renderer.createElement('link');
      this.renderer.setAttribute(this.linkTag, 'rel', 'canonical');
      this.renderer.appendChild(this.dom.head, this.linkTag);
    }
    // Update the href attribute with the new URL
    this.renderer.setAttribute(this.linkTag, 'href', url);
  }
  removeCanonicalURL() {
    const canonicalLink = this.dom.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.remove();
    }
  }
}
